<template>
  <div>
    <v-card-title class="results-title">
      <span class="text-h5 text-primary font-weight-bold mt-5 pt-7">
        Results
      </span>
    </v-card-title>

    <v-card-text class="violations__body-container">
      <v-container
          fluid
          class="px-0"
      >
        <v-row dense>
          <!-- SUCCESS/ERROR STATE -->
          <v-col v-if="!getLoading && !isInitialState && violationsSuccess">
            <v-alert
                outlined
                :type="getRequestError ? 'error' : 'success'"
                text
            >
              <span v-if="getRequestError" class="request-error-message">
                {{ getRequestError }}
              </span>
              <span v-else>
                Your metadata is DCAT-AP compliant.
              </span>
            </v-alert>
          </v-col>
          <!-- EMPTY STATE -->
          <v-col v-else-if="isInitialState && violations.length === 0">
            <div class="py-5 mt-5">
              Validation results will be displayed here.
            </div>
          </v-col>
          <v-col
              v-for="(violation, index) in violations"
              v-else
              :key="index"
              cols="12"
          >
            <v-card
                class="violation-card"
            >
              <v-card-title class="d-sm-flex justify-sm-space-between">
                <div class="text-body-2 font-weight-bold">
                  <v-chip
                      class="ma-2 ml-0"
                      :color="resultColors[violation.resultSeverity]"
                  >
                    {{ violation.resultSeverity.replace('sh:', '') }}
                  </v-chip>
                </div>
              </v-card-title>

              <v-card-subtitle>
                <span class="text--darken-1">
                  Issue in property
                  <strong>{{ violation.resultPath }}</strong>
                  <br>
                  of node
                  <a :href="violation.focusNode">{{ violation.focusNode }}</a>
                </span>
              </v-card-subtitle>

              <v-card-text>
                <strong class="text-primary mb-3">
                  {{ violation.resultMessage }}
                </strong>
                <div class="mt-3" v-if="violation.value">
                  but is
                  <strong class="text-primary mb-3">
                    {{ violation.value }}
                  </strong>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </div>
</template>

<script>

import {mapGetters} from 'vuex';

export default {
  name: 'Violations',

  props: {
    violations: {
      type: Array,
      default: () => [],
    },
    violationsCount: {
      type: Number,
      default: 0,
    },
    validationSource: {
      // Used to determine whether something has been validated yet
      // Can be either 'editor', 'url', or null
      // Useful for jumping to specific code lines when validating with editor
      type: String,
      default: null,
    },
  },

  data() {
    return {
      resultColors: {
        'sh:Violation': 'error',
        'sh:Warning': 'warning',
        'sh:Info': 'info',
      },
    };
  },

  computed: {
    ...mapGetters('violations', [
      'getViolations',
      'getLoading',
      'getRequestError',
    ]),
    violationsSuccess() {
      return this.violationsCount === 0;
    },
    // Returns existing violation type keys
    nonZeroViolationKeys() {
      return Object.keys(this.violationsCount).filter((v) => this.violationsCount[v] > 0);
    },
    isInitialState() {
      // Once we have a validationSource (i.e editor or url) we know
      // that the state is not initial anymore
      return !this.validationSource;
    },
  },

  watch: {
    getRequestError(error) {
      if (error) {
        this.$store.dispatch('snackbar/showError', error);
      }
    },
  },

  methods: {
    parseViolationLine(start, end) {
      const line = `Line ${start}`;
      return !end || start === end
          ? line
          : `${line}-${end}`;
    },
    parseViolationPointer(pointer) {
      return pointer
          .replace(/^\//g, '')
          .replace(/\//g, ' > ')
          .replace(/~1/g, '/')
          .replace(/~0/g, '~');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.violations {
  &__body-container {
    max-height: 600px;
    overflow: auto;
  }
}

.results-title {
  border-bottom: 1px solid #cfcfcf;

  span {
    padding-top: 48px !important;
  }
}

.violation-card {
  box-shadow: none !important;
  border-bottom: 1px solid #cfcfcf;

  > div {
    padding-left: 0 !important;
  }
}

.request-error-message {
  white-space: pre-line;
}
</style>
